import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Download, FileText, Shield, ClipboardList, Copy, Check, Search, ChevronDown } from 'lucide-react';
import PrintButton from '../components/PrintButton';
import { matchesNameQuery } from '../lib/nameSearch';

export default function DataExportPage({ user }) {
  const [patients, setPatients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedSection, setCopiedSection] = useState(null);

  // Patient Data Export state
  const [selectedPatient, setSelectedPatient] = useState('');
  const [justificationReason, setJustificationReason] = useState('');
  const [patientDropOpen, setPatientDropOpen] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const patientDropRef = useRef(null);

  // Clinical Summary patient dropdown
  const [clinicalDropOpen, setClinicalDropOpen] = useState(false);
  const [clinicalSearch, setClinicalSearch] = useState('');
  const clinicalDropRef = useRef(null);
  const [patientExportResult, setPatientExportResult] = useState(null);

  // Billing Report state
  const [billingStartDate, setBillingStartDate] = useState('');
  const [billingEndDate, setBillingEndDate] = useState('');
  const [billingStatus, setBillingStatus] = useState('all');
  const [billingExportResult, setBillingExportResult] = useState(null);

  // Audit Log state
  const [auditStartDate, setAuditStartDate] = useState('');
  const [auditEndDate, setAuditEndDate] = useState('');
  const [auditUser, setAuditUser] = useState('');
  const [auditAction, setAuditAction] = useState('');
  const [auditExportResult, setAuditExportResult] = useState(null);

  // Clinical Summary state
  const [clinicalPatient, setClinicalPatient] = useState('');
  const [clinicalSummary, setClinicalSummary] = useState(null);

  useEffect(() => {
    fetchPatients();
    fetchUsers();
  }, []);

  const fetchPatients = async () => {
    try {
      const result = await window.zignagenixAPI.getPatients();
      setPatients(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const result = await window.zignagenixAPI.getUsers();
      setUsers(Array.isArray(result) ? result : []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (patientDropRef.current && !patientDropRef.current.contains(e.target)) setPatientDropOpen(false);
      if (clinicalDropRef.current && !clinicalDropRef.current.contains(e.target)) setClinicalDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Alphabetically sorted + grouped + filtered patients
  const buildGrouped = (searchTerm) => {
    const sorted = [...patients].sort((a, b) => {
      const la = (a.last_name || '').toLowerCase(), lb = (b.last_name || '').toLowerCase();
      if (la !== lb) return la.localeCompare(lb);
      return (a.first_name || '').toLowerCase().localeCompare((b.first_name || '').toLowerCase());
    });
    const term = (searchTerm || '').toLowerCase().trim();
    const filtered = term
      ? sorted.filter(p => matchesNameQuery([p.last_name, p.first_name, p.mrn], term))
      : sorted;
    const groups = {};
    for (const p of filtered) {
      const letter = (p.last_name || p.first_name || '#')[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(p);
    }
    return { sorted: filtered, groups, letters: Object.keys(groups).sort() };
  };

  const patientGrouped = useMemo(() => buildGrouped(patientSearch), [patients, patientSearch]);
  const clinicalGrouped = useMemo(() => buildGrouped(clinicalSearch), [patients, clinicalSearch]);

  const getPatientLabel = (id) => {
    const p = patients.find(pt => String(pt.id) === String(id));
    if (!p) return '';
    return `${p.last_name || ''}, ${p.first_name || ''} — MRN: ${p.mrn || p.id}`;
  };

  // Reusable alphabetical dropdown renderer
  const renderPatientDropdown = ({ value, setValue, open, setOpen, search, setSearch, grouped, dropRef, idPrefix }) => (
    <div className="form-group" style={{ position: 'relative' }} ref={dropRef}>
      <label className="form-label">Select Patient</label>
      <div
        className="form-input"
        style={{
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          minHeight: '38px', userSelect: 'none',
        }}
        onClick={() => setOpen(!open)}
      >
        <span style={{ color: value ? '#111827' : '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {getPatientLabel(value) || '— Choose a patient —'}
        </span>
        <ChevronDown size={16} color="#6b7280" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', borderRadius: '0.5rem', marginTop: '4px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 4px 10px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb', maxHeight: '360px', display: 'flex', flexDirection: 'column',
        }}>
          {/* Search */}
          <div style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Search size={14} color="#9ca3af" />
            <input
              type="text" placeholder="Search by name or MRN..." value={search}
              onChange={e => setSearch(e.target.value)} autoFocus
              style={{ border: 'none', outline: 'none', flex: 1, fontSize: '0.875rem', background: 'transparent', color: '#111827' }}
              onClick={e => e.stopPropagation()}
            />
            {search && <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>{grouped.sorted.length} results</span>}
          </div>

          {/* Letter jump bar */}
          {grouped.letters.length > 3 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', padding: '0.35rem 0.5rem', borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
              {grouped.letters.map(letter => (
                <button key={letter} onClick={(e) => { e.stopPropagation(); document.getElementById(`${idPrefix}-group-${letter}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                  style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '3px', padding: '1px 6px', fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', color: '#2563eb', lineHeight: 1.4 }}>
                  {letter}
                </button>
              ))}
            </div>
          )}

          {/* Grouped list */}
          <div style={{ overflowY: 'auto', maxHeight: '270px' }}>
            {grouped.sorted.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                {patients.length === 0 ? 'No patients found' : 'No matches'}
              </div>
            ) : grouped.letters.map(letter => (
              <div key={letter} id={`${idPrefix}-group-${letter}`}>
                <div style={{ padding: '0.25rem 0.75rem', background: '#f3f4f6', fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', position: 'sticky', top: 0, zIndex: 1 }}>
                  {letter}
                </div>
                {grouped.groups[letter].map(p => (
                  <div key={p.id}
                    onClick={() => { setValue(String(p.id)); setOpen(false); setSearch(''); }}
                    style={{
                      padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', color: '#111827',
                      background: String(p.id) === String(value) ? '#eff6ff' : 'transparent',
                      borderLeft: String(p.id) === String(value) ? '3px solid #2563eb' : '3px solid transparent',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}
                    onMouseEnter={e => { if (String(p.id) !== String(value)) e.currentTarget.style.background = '#f9fafb'; }}
                    onMouseLeave={e => { if (String(p.id) !== String(value)) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span><strong>{p.last_name || ''}</strong>{p.last_name && p.first_name ? ', ' : ''}{p.first_name || ''}</span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>MRN: {p.mrn || p.id}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const hasPermission = (permission) => {
    return user?.permissions?.includes(permission) || false;
  };

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  // Patient Data Export
  const handleExportPatientData = async () => {
    if (!selectedPatient || !justificationReason.trim()) {
      alert('Please select a patient and provide a justification reason.');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.exportPatientData(selectedPatient, justificationReason);
      setPatientExportResult(result);
    } catch (error) {
      console.error('Error exporting patient data:', error);
      alert('Failed to export patient data');
    } finally {
      setLoading(false);
    }
  };

  // Billing Report Export
  const handleExportBillingReport = async () => {
    const filters = {
      startDate: billingStartDate,
      endDate: billingEndDate,
      status: billingStatus
    };

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.exportBillingReport(filters);
      setBillingExportResult(result);
    } catch (error) {
      console.error('Error exporting billing report:', error);
      alert('Failed to export billing report');
    } finally {
      setLoading(false);
    }
  };

  // Audit Log Export
  const handleExportAuditLog = async () => {
    const filters = {
      startDate: auditStartDate,
      endDate: auditEndDate,
      user: auditUser,
      action: auditAction
    };

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.exportAuditLog(filters);
      setAuditExportResult(result);
    } catch (error) {
      console.error('Error exporting audit log:', error);
      alert('Failed to export audit log');
    } finally {
      setLoading(false);
    }
  };

  // Clinical Summary Export
  const handleExportClinicalSummary = async () => {
    if (!clinicalPatient) {
      alert('Please select a patient.');
      return;
    }

    setLoading(true);
    try {
      const result = await window.zignagenixAPI.exportClinicalSummary(clinicalPatient);
      setClinicalSummary(result);
    } catch (error) {
      console.error('Error exporting clinical summary:', error);
      alert('Failed to export clinical summary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header-row">
        <h1 className="page-title">Data Export & Compliance Reporting</h1>
        <PrintButton pageTitle="Data Export & Compliance Reporting" />
      </div>

      {/* Patient Data Export */}
      {hasPermission('export:patients') && (
        <section className="export-section">
          <div className="export-header">
            <FileText size={24} />
            <h2>Patient Data Export</h2>
          </div>
          {renderPatientDropdown({
            value: selectedPatient, setValue: setSelectedPatient,
            open: patientDropOpen, setOpen: setPatientDropOpen,
            search: patientSearch, setSearch: setPatientSearch,
            grouped: patientGrouped, dropRef: patientDropRef, idPrefix: 'export-patient',
          })}

          <div className="form-group">
            <label className="form-label">Justification Reason *</label>
            <textarea
              className="form-input"
              rows="4"
              placeholder="Provide a compliance-approved reason for exporting this patient's data..."
              value={justificationReason}
              onChange={(e) => setJustificationReason(e.target.value)}
            />
          </div>

          <button
            className="btn-primary"
            onClick={handleExportPatientData}
            disabled={loading}
          >
            <Download size={18} /> Export Patient Data
          </button>

          {patientExportResult && (
            <div className="export-result">
              <h3>Export Result</h3>
              <div className="preview-pane">
                <pre>{JSON.stringify(patientExportResult, null, 2)}</pre>
              </div>
              <button
                className="btn-secondary btn-sm"
                onClick={() => copyToClipboard(JSON.stringify(patientExportResult, null, 2), 'patient')}
              >
                {copiedSection === 'patient' ? (
                  <>
                    <Check size={16} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={16} /> Copy to Clipboard
                  </>
                )}
              </button>
            </div>
          )}
        </section>
      )}

      {/* Billing Report Export */}
      {hasPermission('export:billing') && (
        <section className="export-section">
          <div className="export-header">
            <FileText size={24} />
            <h2>Billing Report Export</h2>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={billingStartDate}
                onChange={(e) => setBillingStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={billingEndDate}
                onChange={(e) => setBillingEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Status Filter</label>
            <select
              className="form-input"
              value={billingStatus}
              onChange={(e) => setBillingStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <button
            className="btn-primary"
            onClick={handleExportBillingReport}
            disabled={loading}
          >
            <Download size={18} /> Export Billing Report
          </button>

          {billingExportResult && (
            <div className="export-result">
              <h3>Report Summary</h3>
              <div className="stats-grid">
                {billingExportResult.summary && Object.entries(billingExportResult.summary).map(([key, value]) => (
                  <div key={key} className="stat-item">
                    <span className="stat-label">{key}</span>
                    <span className="stat-value">{value}</span>
                  </div>
                ))}
              </div>
              <div className="preview-pane">
                <pre>{JSON.stringify(billingExportResult, null, 2)}</pre>
              </div>
              <button
                className="btn-secondary btn-sm"
                onClick={() => copyToClipboard(JSON.stringify(billingExportResult, null, 2), 'billing')}
              >
                {copiedSection === 'billing' ? (
                  <>
                    <Check size={16} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={16} /> Copy JSON
                  </>
                )}
              </button>
            </div>
          )}
        </section>
      )}

      {/* Audit Log Export */}
      {hasPermission('export:audit') && (
        <section className="export-section">
          <div className="export-header">
            <Shield size={24} />
            <h2>Audit Log Export</h2>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={auditStartDate}
                onChange={(e) => setAuditStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={auditEndDate}
                onChange={(e) => setAuditEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">User Filter</label>
              <select
                className="form-input"
                value={auditUser}
                onChange={(e) => setAuditUser(e.target.value)}
              >
                <option value="">-- All users --</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.display_name || u.username}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Action Filter</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g., view, edit, delete"
                value={auditAction}
                onChange={(e) => setAuditAction(e.target.value)}
              />
            </div>
          </div>

          <button
            className="btn-primary"
            onClick={handleExportAuditLog}
            disabled={loading}
          >
            <Download size={18} /> Export Audit Log
          </button>

          {auditExportResult && (
            <div className="export-result">
              <h3>Audit Log Results</h3>
              <p className="result-count">
                Total entries: <strong>{auditExportResult.count || 0}</strong>
              </p>
              <div className="preview-pane">
                <pre>{JSON.stringify(auditExportResult.entries?.slice(0, 10), null, 2)}</pre>
              </div>
              <p className="text-muted">Showing first 10 entries</p>
            </div>
          )}
        </section>
      )}

      {/* Clinical Summary Export */}
      {hasPermission('export:clinical') && (
        <section className="export-section">
          <div className="export-header">
            <ClipboardList size={24} />
            <h2>Clinical Summary Export</h2>
          </div>

          {renderPatientDropdown({
            value: clinicalPatient, setValue: setClinicalPatient,
            open: clinicalDropOpen, setOpen: setClinicalDropOpen,
            search: clinicalSearch, setSearch: setClinicalSearch,
            grouped: clinicalGrouped, dropRef: clinicalDropRef, idPrefix: 'clinical-patient',
          })}

          <button
            className="btn-primary"
            onClick={handleExportClinicalSummary}
            disabled={loading}
          >
            <Download size={18} /> Generate Clinical Summary
          </button>

          {clinicalSummary && (
            <div className="export-result">
              <h3>Clinical Summary</h3>
              <div className="clinical-summary">
                {clinicalSummary.sections?.map((section, idx) => (
                  <div key={idx} className="summary-section">
                    <h4>{section.title}</h4>
                    <p>{section.content}</p>
                  </div>
                ))}
              </div>
              <button
                className="btn-secondary btn-sm"
                onClick={() => copyToClipboard(JSON.stringify(clinicalSummary, null, 2), 'clinical')}
              >
                {copiedSection === 'clinical' ? (
                  <>
                    <Check size={16} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={16} /> Copy Summary
                  </>
                )}
              </button>
            </div>
          )}
        </section>
      )}

      {!hasPermission('export:patients') && !hasPermission('export:billing') && !hasPermission('export:audit') && !hasPermission('export:clinical') && (
        <div className="permission-denied">
          <Shield size={48} />
          <p>You do not have permission to export data.</p>
        </div>
      )}

      <style jsx>{`
        .export-section {
          background: #F8F9FA;
          border: none;
          box-shadow: 5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85);
          border-radius: 8px;
          padding: 24px;
          margin-bottom: 24px;
        }

        .export-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          border-bottom: none;
          box-shadow: inset 0 -2px 4px rgba(186,199,216,0.45), inset 0 2px 4px rgba(255,255,255,0.85);
          padding-bottom: 16px;
        }

        .export-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 16px;
        }

        .export-result {
          margin-top: 20px;
          padding: 16px;
          background: #F8F9FA;
          border-radius: 6px;
          border: none;
          box-shadow: 3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85);
        }

        .export-result h3 {
          margin: 0 0 16px 0;
          font-size: 16px;
          color: #1f2937;
        }

        .preview-pane {
          background: #F8F9FA;
          border: none;
          box-shadow: inset 2px 2px 4px rgba(186,199,216,0.45), inset -2px -2px 4px rgba(255,255,255,0.85);
          border-radius: 4px;
          padding: 12px;
          margin: 16px 0;
          max-height: 300px;
          overflow-y: auto;
          font-family: 'Monaco', 'Courier New', monospace;
          font-size: 12px;
          color: #374151;
        }

        .preview-pane pre {
          margin: 0;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 12px;
          margin-bottom: 16px;
        }

        .stat-item {
          background: #F8F9FA;
          padding: 12px;
          border-radius: 4px;
          border: none;
          box-shadow: 3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85);
        }

        .stat-label {
          display: block;
          font-size: 12px;
          color: #6C757D;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          display: block;
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          margin-top: 4px;
        }

        .result-count {
          margin: 0 0 16px 0;
          color: #4b5563;
        }

        .clinical-summary {
          background: #F8F9FA;
          border: none;
          box-shadow: 3px 3px 6px rgba(186,199,216,0.45), -3px -3px 6px rgba(255,255,255,0.85);
          border-radius: 4px;
          padding: 16px;
          margin: 16px 0;
        }

        .summary-section {
          margin-bottom: 16px;
        }

        .summary-section:last-child {
          margin-bottom: 0;
        }

        .summary-section h4 {
          margin: 0 0 8px 0;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .summary-section p {
          margin: 0;
          font-size: 14px;
          color: #4b5563;
          line-height: 1.5;
        }

        .text-muted {
          font-size: 12px;
          color: #6C757D;
          margin-top: 8px;
        }

        .permission-denied {
          text-align: center;
          padding: 48px;
          color: #6C757D;
        }

        .permission-denied p {
          margin-top: 16px;
          font-size: 16px;
        }
      `}</style>
    </div>
  );
}
